import { ActionIcon, Flex, Text } from "@mantine/core";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  mutatePurchaseOrderItems,
  purchaseOrderItems,
} from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { DeliveredForm } from "@/features/purchaseOrderItems/delivered/deliveredForm";
import { PurchaseOrderItemsUpdateForm } from "@/features/purchaseOrderItems/form/updateForm";
import { ProductionPlanForm } from "@/features/purchaseOrderItems/productionPlan/productionPlanForm";
import { StockPlanForm } from "@/features/purchaseOrderItems/stockPlan/stockPlanForm";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import customAlert from "@/features/ui/alert/alert";
import { MantineReactCommonTable } from "@/features/ui/mantineTable";
import { useUserStore } from "@/store/userStore";
import { translateWithPartnerType } from "@/utils/partnerType";
import timeUtil from "@/utils/timeUtil";
import { ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { AuthSignupPost201ResponseEquipmentPurchaseOrderItem } from "@sizlcorp/sizl-api-document/dist/models/src/model";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "mantine-react-table";
import { useTranslation } from "react-i18next";

interface PurchaseOrderDetailTableProps {
  onRowSelect: (selectedRows: ReadonlySet<any>, formatterPropsData: any) => void;
  purchaseOrderId: number;
}

export const PurchaseOrderDetailTable = ({
  onRowSelect,
  purchaseOrderId,
}: PurchaseOrderDetailTableProps) => {
  const { t } = useTranslation();
  const { userInfo } = useUserStore();
  const partnerType = userInfo?.partner?.type;

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  });
  // 각 컬럼 sort
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  // 각 컬럼 filter
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  // 전체 검색 (단, serchFields에 필드를 직접 입력해줘야함.)
  const [globalFilter, setGlobalFilter] = useState("");

  // sort를 id 별 string으로 만들어줌
  const sortString = sorting.map(({ id, desc }) => (desc ? `-${id}` : id)).join(",");

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  // filter를 id 별 like를 사용하여 단어가 포함되어있는지 확인
  const filterQuery: { [key: string]: { $iLike: string } } = columnFilters.reduce<{
    [key: string]: { $iLike: string };
  }>((acc, filter) => {
    acc[filter.id] = { $iLike: `%${filter.value}%` as string };
    return acc;
  }, {});

  useEffect(() => {
    const ids = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    setSelectedRowIds(ids);
  }, [rowSelection]);

  const { openModal, closeModal } = useModal();
  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest) =>
      mutatePurchaseOrderItems
        .delete(params)
        .mutationFn(params as any | ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest),
    {
      onSuccess: () => {
        refetch();
        closeModal(null);
        customAlert(
          t("message.deleteSuccess", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          t("common.deleteSuccess"),
          "green"
        );
        selectedRowsRef.current = new Set();
      },
      onError: () => {
        customAlert(
          t("message.deleteFail", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          t("common.deleteFail", {
            item: translateWithPartnerType(t, "purchaseOrderItems.purchaseOrderItems", partnerType),
          }),
          "red"
        );
      },
    }
  );

  const { data: purchaseData, refetch } = useQuery(
    purchaseOrderItems.get({
      query: {
        $and: [
          { purchaseOrderId: purchaseOrderId },
          { direction: { $eq: "INBOUND" } },
          ...Object.keys(filterQuery).map((key) => ({ [key]: filterQuery[key] })),
        ],
      },
      pageSize: pagination.pageSize,
      page: pagination.pageIndex,
      search: globalFilter,
      sort: sortString || undefined,
      populate: ["itemName", "user", "quantities", "location"],
    })
  );

  const rows: readonly AuthSignupPost201ResponseEquipmentPurchaseOrderItem[] =
    purchaseData?.data.rows ?? [];
  const selectedRowsRef = useRef<ReadonlySet<any>>(new Set());

  useEffect(() => {
    const selectedData = rows.filter((row) => selectedRowIds.includes(String(row.id)));
    onRowSelect(selectedRowsRef.current, selectedData);
  }, [selectedRowIds, rows]);

  const columns = useMemo<MRT_ColumnDef<AuthSignupPost201ResponseEquipmentPurchaseOrderItem>[]>(
    () => [
      {
        accessorKey: "itemName",
        header: t("item.name"),
        Cell: ({ row }) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <ItemsDetailForm itemCode={row?.original?.itemCode} />,
                  null,
                  t("common.detail", { item: t("item.item") })
                )
              }
            >
              {row?.original?.itemName}
            </LinkAnchor>
          );
        },
      },
      {
        accessorKey: "quantity",
        header: t("inventory.quantity"),
        enableColumnFilter: false,
        size: 100,
        mantineTableBodyCellProps: { style: { textAlign: "right" } },
      },
      {
        accessorKey: "sumOfProductionPlanQuantity",
        header: t("purchaseOrderItems.sumOfProductionPlanQuantity"),
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => {
          const productionPlanQuantity = row?.original?.sumOfProductionPlanQuantity;
          return (
            <Flex justify="flex-end">
              {productionPlanQuantity === "0" ? (
                <Text>{productionPlanQuantity}</Text>
              ) : (
                <LinkAnchor
                  onClick={() => {
                    openModal(
                      <ProductionPlanForm purchaseOrderItemID={row.original.id} />,
                      null,
                      t("common.detail", { item: t("productionPlan.productionPlan") })
                    );
                  }}
                >
                  {productionPlanQuantity}
                </LinkAnchor>
              )}
            </Flex>
          );
        },
      },
      {
        accessorKey: "sumOfStockPlanQuantity",
        header: translateWithPartnerType(
          t,
          "purchaseOrderItems.sumOfStockPlanQuantity",
          partnerType
        ),
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => {
          const stockPlanQuantity = row?.original?.sumOfStockPlanQuantity;
          return (
            <Flex justify="flex-end">
              {stockPlanQuantity === "0" ? (
                <Text>{stockPlanQuantity}</Text>
              ) : (
                <LinkAnchor
                  onClick={() => {
                    openModal(
                      <StockPlanForm purchaseOrderItemID={row.original.id} />,
                      null,
                      t("common.detail", { item: t("stockPlan.stockPlan") })
                    );
                  }}
                >
                  {stockPlanQuantity}
                </LinkAnchor>
              )}
            </Flex>
          );
        },
      },
      {
        accessorKey: "sumOfDeliveredQuantity",
        header: translateWithPartnerType(
          t,
          "purchaseOrderItems.sumOfDeliveredQuantity",
          partnerType
        ),
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => {
          return (
            <Flex justify="flex-end">
              <LinkAnchor
                onClick={() => {
                  openModal(
                    <DeliveredForm purchaseOrderItemID={row.original.id} />,
                    null,
                    t("common.detail", { item: t("delivery.deliveryComplete") })
                  );
                }}
              >
                {row?.original?.sumOfDeliveredQuantity}
              </LinkAnchor>
            </Flex>
          );
        },
      },
      {
        accessorKey: "sourceLocationName",
        header: translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType),
        Cell: ({ row }) => {
          return (
            <LinkAnchor
              onClick={() =>
                openModal(
                  <LocationDetailForm locationCode={row?.original?.sourceLocationCode} />,
                  null,
                  t("common.detail", { item: t("location.location") })
                )
              }
            >
              {row?.original?.sourceLocationName}
            </LinkAnchor>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: t("orderItems.createdAt"),
        Cell: ({ row }) => {
          return (
            <div>
              {row?.original?.createdAt !== null
                ? timeUtil(row?.original?.createdAt as string)
                : ""}
            </div>
          );
        },
      },
      {
        accessorKey: "deliveryDeadline",
        header: t("order.deliveryDate"),
        Cell: ({ row }) => {
          return (
            <div>
              {row?.original?.deliveryDeadline !== null
                ? timeUtil(row?.original?.deliveryDeadline as string)
                : ""}
            </div>
          );
        },
      },
    ],
    [t]
  );

  return (
    <MantineReactCommonTable
      data={rows as AuthSignupPost201ResponseEquipmentPurchaseOrderItem[]}
      columns={columns}
      totalCount={purchaseData?.data.total}
      totalPages={purchaseData?.data.totalPages}
      pagination={pagination} // pagination
      setPagination={setPagination}
      sorting={sorting} // 정렬
      setSorting={setSorting}
      columnFilters={columnFilters} // 컬럼별 검색
      setColumnFilters={setColumnFilters}
      globalFilter={globalFilter} // 전체 검색
      setGlobalFilter={setGlobalFilter}
      rowSelection={rowSelection} // 선택
      setRowSelection={setRowSelection}
      selectedRowIds={selectedRowIds} // 선택된 id
      setSelectedRowIds={setSelectedRowIds}
      enableRowSelection={true} // 선택 가능 여부
      enableTopToolbar={true}
      enableRowActions={true}
      positionActionsColumn
      renderRowActions={({ row }) => {
        return (
          <Flex gap="md">
            <ActionIcon
              variant="subtle"
              color="yellow"
              onClick={() => {
                openModal(
                  <PurchaseOrderItemsUpdateForm formatterProps={row.original} />,
                  null,
                  t("common.edit", {
                    item: translateWithPartnerType(
                      t,
                      "purchaseOrderItems.purchaseOrderItems",
                      partnerType
                    ),
                  })
                );
              }}
            >
              <IconEdit />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => {
                deleteMutate({ purchaseOrderItemId: row.original.id ?? 0 });
              }}
            >
              <IconTrash />
            </ActionIcon>
          </Flex>
        );
      }}
    />
  );
};
